import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../App';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    console.log('[AuthContext] checkAuth - token exists:', !!token);
    if (token) {
      try {
        const response = await api.get('/auth/me');
        console.log('[AuthContext] checkAuth - user:', response.data.email);
        setUser(response.data);
        
        // Fetch subscription status
        try {
          const subResponse = await api.get('/auth/subscription-status');
          setSubscriptionStatus(subResponse.data);
        } catch (subError) {
          console.error('[AuthContext] subscription status error:', subError.message);
        }
      } catch (error) {
        console.error('[AuthContext] checkAuth - error:', error.message);
        localStorage.removeItem('token');
        setUser(null);
        setSubscriptionStatus(null);
      }
    }
    setLoading(false);
  };

  // Generate device fingerprint
  const getDeviceFingerprint = () => {
    let fingerprint = localStorage.getItem('device_fingerprint');
    if (!fingerprint) {
      // Create a unique device ID based on browser characteristics
      const nav = navigator;
      const screen = window.screen;
      const deviceData = [
        nav.userAgent,
        nav.language,
        screen.width,
        screen.height,
        screen.colorDepth,
        new Date().getTimezoneOffset(),
        !!window.sessionStorage,
        !!window.localStorage
      ].join('|');
      
      // Simple hash function
      let hash = 0;
      for (let i = 0; i < deviceData.length; i++) {
        const char = deviceData.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      
      fingerprint = 'device_' + Math.abs(hash) + '_' + Date.now();
      localStorage.setItem('device_fingerprint', fingerprint);
    }
    return fingerprint;
  };

  const login = async (email, password) => {
    try {
      console.log('[AuthContext] login - attempting login for:', email);
      const deviceInfo = getDeviceFingerprint();
      const response = await api.post('/auth/login', { 
        email, 
        password,
        device_info: deviceInfo 
      });
      console.log('[AuthContext] login - response received, token:', !!response.data.token);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        console.log('[AuthContext] login - user set:', response.data.user.email);
        
        // Fetch subscription status after login
        try {
          const subResponse = await api.get('/auth/subscription-status');
          setSubscriptionStatus(subResponse.data);
        } catch (subError) {
          console.error('[AuthContext] subscription status error:', subError.message);
        }
      }
      return response.data;
    } catch (error) {
      console.error('[AuthContext] login error:', error);
      
      // Handle service temporarily unavailable (MongoDB timeout)
      if (error.response?.status === 503) {
        const customError = new Error(error.response?.data?.detail || 'Service temporarily unavailable, please try again.');
        customError.response = error.response;
        throw customError;
      }
      
      // Handle session limit exceeded error
      if (error.response?.status === 403 && error.response?.data?.detail === 'device_limit_exceeded') {
        const customError = new Error('Session limit exceeded. This account already has 2 active sessions. Please log out from another session to continue.');
        customError.isSessionLimitError = true;
        throw customError;
      }
      
      // Handle email not verified error
      if (error.response?.status === 403 && error.response?.data?.detail === 'email_not_verified') {
        const customError = new Error('Please verify your email before logging in. Check your inbox for the verification code.');
        customError.isEmailNotVerified = true;
        throw customError;
      }
      
      throw error;
    }
  };

  const register = async (email, password, full_name, referral_token = '') => {
    try {
      const payload = { email, password, full_name };
      if (referral_token && referral_token.trim()) {
        payload.referral_code = referral_token.trim();  // Backend expects 'referral_code'
      }
      const response = await api.post('/auth/register', payload);
      
      // Check if verification is required
      if (response.data.requires_verification) {
        return response.data;  // Return without setting user/token
      }
      
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        
        // Clear onboarding flag for new users to ensure tutorial shows
        localStorage.removeItem('lexprocure_onboarding_completed');
        
        // Fetch subscription status after registration
        try {
          const subResponse = await api.get('/auth/subscription-status');
          setSubscriptionStatus(subResponse.data);
        } catch (subError) {
          console.error('[AuthContext] subscription status error:', subError.message);
        }
      }
      return response.data;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  };

  const verifyEmail = async (email, verificationCode) => {
    try {
      const response = await api.post('/auth/verify-email', { 
        email, 
        verification_code: verificationCode 
      });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        
        // Clear onboarding flag for new users to ensure tutorial shows
        localStorage.removeItem('lexprocure_onboarding_completed');
      }
      return response.data;
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await api.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      console.error('Resend verification error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call backend to remove device session
      await api.post('/auth/logout');
    } catch (error) {
      console.error('[AuthContext] logout error:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem('token');
      setUser(null);
      setSubscriptionStatus(null);
    }
  };

  const value = {
    user,
    subscriptionStatus,
    login,
    register,
    logout,
    loading,
    checkAuth,
    verifyEmail,
    resendVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
